import { useState, useEffect } from "react";
import axios from "axios";
import AsyncSelect from 'react-select/async';
import Sidebar from "../../../components/sidebar/Sidebar";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { PAYMENT_METHODS } from "../../../utils/paymentMethods";
import { getSelectStyles } from "../../../utils/selectStyles";
import "../../accounts/create/CreateAccountPage.css";

export default function CreateTransactionPage() {
  const [accounts, setAccounts]   = useState([]);
  const [created, setCreated]     = useState(false);
  const [loading, setLoading]     = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]         = useState(null);
  const [formError, setFormError] = useState(null);

  const [transaction, setTransaction] = useState({
    accountID: "", revenue: 0, profit: 0, notes: "",
    transactionDate: null, paymentMethod: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/account`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        setAccounts(res.data);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to fetch accounts");
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'transactionDate') {
      setTransaction(prev => ({ ...prev, [name]: value ? new Date(value).getTime() : null }));
    } else {
      setTransaction(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleAccountSelect = (opt) => {
    setTransaction(prev => ({ ...prev, accountID: opt ? opt.value : "" }));
  };

  const loadOptions = (inputValue, callback) => {
    const filtered = accounts.filter(a =>
      a.username.toLowerCase().includes(inputValue.toLowerCase()) ||
      a.email?.toLowerCase().includes(inputValue.toLowerCase()) ||
      a.discord?.toLowerCase().includes(inputValue.toLowerCase())
    );
    callback(filtered.map(a => ({ value: a._id, label: `${a.username}${a.email ? ` (${a.email})` : ''}` })));
  };

  const formatDateForInput = (unixMs) =>
    unixMs ? new Date(unixMs).toISOString().split('T')[0] : '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setCreated(false);

    if (!transaction.accountID) return setFormError("Please select an account.");
    if (!transaction.transactionDate) return setFormError("Please select a transaction date.");
    if (!transaction.paymentMethod) return setFormError("Please select a payment method.");

    setSubmitting(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/transaction/create`,
        {
          accountID: transaction.accountID,
          revenue: Number(transaction.revenue),
          profit: Number(transaction.profit),
          notes: transaction.notes,
          transactionDate: transaction.transactionDate + "",
          paymentMethod: transaction.paymentMethod,
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setCreated(true);
      setTransaction({ accountID: "", revenue: 0, profit: 0, notes: "", transactionDate: null, paymentMethod: "" });
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create transaction");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <Sidebar title="Create Transaction" />
      <div className="form-page">
        <div className="form-panel">

          <div className="form-panel-header">
            <div>
              <div className="form-panel-title">Create Transaction</div>
              <div className="form-panel-subtitle">Add a new transaction record</div>
            </div>
          </div>

          <div className="form-panel-body">
            <form onSubmit={handleSubmit} noValidate>

              <div className="form-section-label">Account & Date</div>
              <div className="row g-4 mb-2">
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-person-fill" />Account</label>
                    <AsyncSelect
                      cacheOptions defaultOptions
                      loadOptions={loadOptions}
                      onChange={handleAccountSelect}
                      placeholder="Search for an account..."
                      isClearable
                      styles={getSelectStyles()}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-calendar-event" />Transaction Date</label>
                    <input className="form-input" type="date" name="transactionDate"
                      value={formatDateForInput(transaction.transactionDate)} onChange={handleChange} required />
                  </div>
                </div>
              </div>

              <div className="form-section-label" style={{ marginTop: 8 }}>Financials</div>
              <div className="row g-4 mb-2">
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-cash-stack" />Revenue</label>
                    <div className="form-input-group">
                      <span className="form-input-prefix">€</span>
                      <input className="form-input" type="number" name="revenue"
                        value={transaction.revenue} onChange={handleChange} step="0.01" min="0" required />
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-graph-up-arrow" />Profit</label>
                    <div className="form-input-group">
                      <span className="form-input-prefix">€</span>
                      <input className="form-input" type="number" name="profit"
                        value={transaction.profit} onChange={handleChange} step="0.01" required />
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-section-label" style={{ marginTop: 8 }}>Payment & Notes</div>
              <div className="row g-4">
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-credit-card" />Payment Method</label>
                    <select className="form-input" name="paymentMethod"
                      value={transaction.paymentMethod} onChange={handleChange}>
                      <option value="">Select method…</option>
                      {PAYMENT_METHODS.map(m => (
                        <option key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label"><i className="bi bi-journal-text" />Notes</label>
                    <textarea className="form-input" name="notes" value={transaction.notes || ''}
                      onChange={handleChange} rows={3} placeholder="Additional notes…"
                      style={{ resize: 'vertical' }} />
                  </div>
                </div>
              </div>

              {created    && <div className="form-banner success"><i className="bi bi-check-circle-fill" />Transaction created successfully!</div>}
              {formError  && <div className="form-banner error"><i className="bi bi-exclamation-triangle-fill" />{formError}</div>}
              {error      && <div className="form-banner error"><i className="bi bi-exclamation-triangle-fill" />{error}</div>}

              <div className="form-actions">
                <button type="button" className="btn-form-cancel" onClick={() => window.history.back()}>
                  <i className="bi bi-x-lg" />Cancel
                </button>
                <button type="submit" className="btn-form-submit" disabled={submitting}>
                  <i className={`bi ${submitting ? 'bi-hourglass-split' : 'bi-plus-circle'}`} />
                  {submitting ? 'Creating…' : 'Create Transaction'}
                </button>
              </div>

            </form>
          </div>
        </div>
      </div>
    </>
  );
}
